import { Component, Inject, inject, OnInit } from '@angular/core';
import * as FileSaver from 'file-saver';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NewDeliveryDialogComponent } from 'src/app/stocking/extra-delivery/new-delivery-dialog/new-delivery-dialog.component';
import { EmailService, Email, Attachment } from '../../../services/email.service';
import { NotificationService } from 'src/app/shared/notification.service';
import moment from  'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { StorageService } from 'src/app/shared-services/storage.service';

@Component({
  selector: 'app-email-dialog',
  templateUrl: './email-dialog.component.html',
  styleUrl: './email-dialog.component.css'
})
export class EmailDialogComponent implements OnInit {

  private _formBuilder = inject(FormBuilder);
  loading = false;
  time: string[] = [];
  hours: string[] = new Array(24).fill(0).map((_, i) => (i < 10 ? '0' + i : i.toString()));
  minutes: string[] = new Array(60).fill(0).map((_, i) => (i < 10 ? '0' + i : i.toString()));
  attachmentLimit = 5; // Max number of attachments
  maxAttachmentSize = 30 * 1024 * 1024; // Max attachment size in bytes (30 MB)
  attachments: Attachment[] = [];

  emailFormGroup = this._formBuilder.group({
    // name: ['', Validators.required],
    emailTo: ['', [Validators.required, Validators.email]],
    cc: ['', [Validators.email]],
    subject: ['', Validators.required],
    sendAsHtml: [false],
    title: ['', Validators.required],
    html: [''],
    text: ['', Validators.required],
    attachments: [[]],
    includeSignature: [true],
    signatureType: ['frigg'],
    sendAtDate: [new Date()],
    sendAtHour: [moment().format('HH')],
    sendAtMinute: [moment().format('mm')],
    asap: [true],
  });

  constructor(
    public dialogRef: MatDialogRef<NewDeliveryDialogComponent>, @Inject(MAT_DIALOG_DATA) public data,
    private emailService: EmailService,
    private notifierService: NotificationService,
    private storageService: StorageService
  ) {
  }

  ngOnInit(): void {
    console.log('EmailDialogComponent initialized with data:', this.data);
    // Initialize form with default values
    this.emailFormGroup.get('emailTo')?.setValue(this.data?.email?.emailTo || '');
    this.emailFormGroup.get('cc')?.setValue(this.data?.email?.cc || '');
    this.emailFormGroup.get('subject')?.setValue(this.data?.email?.subject || '');
    this.emailFormGroup.get('title')?.setValue(this.data?.email?.title || '');
    // this.emailFormGroup.get('sendAsHtml')?.setValue( true);
    this.emailFormGroup.get('html')?.setValue(this.data?.email?.html || '');
    this.emailFormGroup.get('text')?.setValue(this.data?.email?.text || '');
    this.emailFormGroup.get('includeSignature')?.setValue(this.data?.email?.includeSignature || true);
    this.emailFormGroup.get('signatureType')?.setValue(this.data?.email?.signatureType || 'frigg');
    this.emailFormGroup.get('sendAsHtml')?.setValue(this.data?.email?.html != '' || false);

    // Load attachments if available
    if (this.data?.email?.attachments && this.data.email.attachments.length > 0) {
      this.attachments = this.data.email.attachments.map(attachment => ({
        file: new File([], attachment.name), // Create a dummy file object
        url: attachment.url, // Store the URL for later use
        fileName: attachment.fileName,
        filePath: attachment.filePath,
        name: attachment.name
      }));
      console.log('Loaded attachments:', this.attachments);
    }

    this.updateHTMLOrNot(this.data?.email?.html != '' || false);

    this.emailFormGroup.get('sendAsHtml').valueChanges.subscribe(value => {
      this.updateHTMLOrNot(value);
    });

    this.emailFormGroup.get('html').valueChanges.subscribe(value => {
      this.updatePreview();
    });

    this.emailFormGroup.get('text').valueChanges.subscribe(value => {
      this.updatePreview();
    });

    this.emailFormGroup.get('title').valueChanges.subscribe(value => {
      this.updatePreview();
    });

    this.emailFormGroup.get('includeSignature').valueChanges.subscribe(value => {
      this.updatePreview();
    });

    this.emailFormGroup.get('signatureType').valueChanges.subscribe(value => {
      this.updatePreview();
    });

    this.emailFormGroup.get('emailTo').valueChanges.subscribe(value => {
      this.updatePreview();
    });

    this.emailFormGroup.get('cc').valueChanges.subscribe(value => {
      this.updatePreview();
    });

    this.emailFormGroup.get('subject').valueChanges.subscribe(value => {
      this.updatePreview();
    });


    this.updatePreview();

  }

  updateHTMLOrNot(value): void {
    if (value) {

      // Update validators
      this.emailFormGroup.get('text').clearValidators();
      this.emailFormGroup.get('title').clearValidators();
      this.emailFormGroup.get('html').setValidators([Validators.required]);
    } else {

      // Update validators
      this.emailFormGroup.get('html').clearValidators();
      this.emailFormGroup.get('text').setValidators([Validators.required]);
      this.emailFormGroup.get('title').setValidators([Validators.required]);

    }

    this.emailFormGroup.get('html').updateValueAndValidity();
    this.emailFormGroup.get('text').updateValueAndValidity();
    this.emailFormGroup.get('title').updateValueAndValidity();

    this.updatePreview();
  }

  updatePreview(): void {
    const div = document.querySelector('#mailContentPreview');

    let html = '';

    // Add to and subject
    html += `<p><b>To:</b> ${this.emailFormGroup.get('emailTo')?.value}</p>`;
    if (this.emailFormGroup.get('cc')?.value) {
      html += `<p><b>CC:</b> ${this.emailFormGroup.get('cc')?.value}</p>`;
    }
    html += `<p><b>Subject:</b> ${this.emailFormGroup.get('subject')?.value}</p><br>`;


    // const htmlTemplate = mail.html? mail.html : `<h4>${mail.title}</h4><p>${mail.text}</p>` 
    if (div) {
      if (this.emailFormGroup.get('sendAsHtml')?.value) {
        html += this.emailFormGroup.get('html')?.value || "<div class='email-preview'>This is a preview of the email content.</div>";
      } else {
        html += `<h4>${this.emailFormGroup.get('title')?.value}</h4><p>${this.emailFormGroup.get('text')?.value}</p>`;
      }
    }
    // `<br><b>Med vennlig hilsen Cuboid AS</b><br><br><img src="cid:logo.png"/><br><br>` : ``);
    if (this.emailFormGroup.get('includeSignature')?.value) {
      if (this.emailFormGroup.get('signatureType')?.value === 'frigg') {
        html += `<br><b>Med vennlig hilsen Friggkantine</b><br><br><img src="/assets/img/frigg-icon-150x42.png"/><br><br>`;
      } else if (this.emailFormGroup.get('signatureType')?.value === 'cuboid') {
        html += `<br><b>Med vennlig hilsen Cuboid AS</b><br><br><img src="/assets/images/logo.png"/><br><br>`;
      };

    }

    div.innerHTML = html;
  }

  onSetTime(): void {
    // this.time = document.getElementById("startTime")['value']
    console.log('Set time clicked ', document.getElementById("startTime")['value']);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onSubmit(): Promise<void> {
    if (this.emailFormGroup.valid) {
      this.loading = true;

      console.log('Email Form Submitted:', this.emailFormGroup.value);

      let attachments = null;

      console.log("attachments", attachments)

      let notUploadedFiles = this.attachments.filter(attachment => !attachment.url);
      let uploadedFiles = this.attachments.filter(attachment => attachment.url);
      console.log('Not uploaded files:', notUploadedFiles);

      console.log('notUploadedFiles files:', notUploadedFiles);
      console.log('uploadedFiles files:', uploadedFiles);

      // Add existing attachments URLs to the email data
      if (uploadedFiles.length > 0) {
        attachments = uploadedFiles.map(file => {
          console.log("file", file)
          return {
            name: file.name,
            url: file.url,
            fileName: file.fileName,
            filePath: file.filePath

          }
        });
      } else {
        attachments = [];
      }

      // Check if attachments are selected
      if (notUploadedFiles.length > 0) {
        console.log('Attachments selected:', notUploadedFiles);
        // Upload attachments to storage and get URLs
        const uploadPromises = notUploadedFiles.map(attachment => {
          const fileName = `${moment().unix()}_${attachment.name}`;
          const filePath = `emails/${fileName}`;
          return this.storageService.uploadFile(filePath, attachment.file).toPromise()
            .then((url: string) => {
              console.log('File uploaded successfully:', url);
              return { name: attachment.file.name, url, filePath, fileName };
            })
            .catch(error => {
              console.error('Error uploading file:', error);
              throw error; // Propagate the error
            });
        });

        await Promise.all(uploadPromises).then(uploadedFiles => {
          console.log('All files uploaded successfully:', uploadedFiles);
          // Add the uploaded file URLs to the email data
          attachments = [...attachments, ...uploadedFiles.map(file => ({
            name: file.name,
            url: file.url,
            filePath: file.filePath,
            fileName: file.fileName
          }))];
        }).catch(error => {
          console.error('Error uploading files:', error);
          this.notifierService.showNotification('error', 'Error uploading files: ' + error.message);
          return; // Exit if there's an error
        });
      }

      const useHtml = this.emailFormGroup.get('sendAsHtml')?.value;

      const sendAtTimeAndDate = moment(this.emailFormGroup.get('sendAtDate')?.value).set({
        hour: Number(this.emailFormGroup.get('sendAtHour')?.value),
        minute: Number(this.emailFormGroup.get('sendAtMinute')?.value)
      });


      const emailData: Email = {
        emailTo: this.emailFormGroup.get('emailTo')?.value,
        cc: this.emailFormGroup.get('cc')?.value,
        subject: this.emailFormGroup.get('subject')?.value,
        title: !useHtml ? this.emailFormGroup.get('title')?.value : '',
        html: useHtml ? this.emailFormGroup.get('html')?.value : '',
        text: !useHtml ? this.emailFormGroup.get('text')?.value : '',
        includeSignature: this.emailFormGroup.get('includeSignature')?.value,
        signatureType: this.emailFormGroup.get('signatureType')?.value,
        asap: this.emailFormGroup.get('asap')?.value,
        sendAtUnix: this.emailFormGroup.get('asap')?.value ? moment().add(10, 'minutes').unix() : sendAtTimeAndDate.unix(),
        attachments: attachments || [],
      };

      console.log('Email data to be sent:', emailData);


      this.emailService.sendEmail(emailData).then((emailId) => {
        console.log('Email sent successfully:', emailId);
        console.log('Successfully added to que:', emailData);
        this.notifierService.showNotification('success', 'Email added to que successfully!');
        this.dialogRef.close(emailData);
      }
      ).catch((error) => {
        console.error('Error sending email:', error);
        this.notifierService.showNotification('error', 'Error sending email: ' + error.message);
        // Optionally, you can show an error message to the user
      });
      //this.dialogRef.close(this.emailFormGroup.value);
      // this.emailService.sendEmail(this.emailFormGroup.value).then(() => {
      //   this.dialogRef.close(this.emailFormGroup.value);
      // }
      // ).catch((error) => {
      //   console.error('Error sending email:', error);
      //   // Optionally, you can show an error message to the user
      // });


    } else {
      this.emailFormGroup.markAllAsTouched();
    }
  }

  onFileSelected() {
    const inputNode: any = document.querySelector('#file');

    if (inputNode.files && inputNode.files.length > 0) {
      const file = inputNode.files[0]; // Get the first selected file

      if (typeof FileReader !== 'undefined') {
        const reader = new FileReader();
        const fileName = file.name;

        reader.onload = (e: any) => {

          // Garding
          if (!e.target || !e.target.result) {
            console.error('FileReader did not return a valid result.');
            return;
          }

          // Check file size
          if (file.size > this.maxAttachmentSize) {
            console.error('File size exceeds the maximum limit of 10 MB.');
            this.notifierService.showNotification('error', 'File size exceeds the maximum limit of 10 MB.');
            return;
          }
          // Check attachment limit
          if (this.attachments.length >= this.attachmentLimit) {
            console.error('Attachment limit reached. Cannot add more than 5 attachments.');
            this.notifierService.showNotification('error', 'Attachment limit reached. Cannot add more than 5 attachments.');
            return;
          }

          const fileBytes = new Uint8Array(e.target.result); // Get the file bytes

          // Add the file to the attachments array
          this.attachments = [...this.attachments, { file, bytes: fileBytes }];


        };

        reader.readAsArrayBuffer(file); // Read the file as an ArrayBuffer
      } else {
        console.error('FileReader is not supported in this environment.');
      }
    } else {
      console.warn('No file selected.');
    }
  }

  downloadAttachment(attachment: { file: File, url?: string }): void {
    if (attachment.url) {

      FileSaver.saveAs(attachment.url, attachment.file.name);
    }
    else {
      console.error('Attachment URL is not available for download:', attachment);
      this.notifierService.showNotification('error', 'Attachment URL is not available for download.');
    }
  }

  removeAttachment(attachment) {
    console.log(attachment);
    this.attachments = this.attachments.filter(_attachment => _attachment.name !== attachment.name)
  }

  // getAttachments() {
  //   console.log('Getting attachments...');
  //   const inputNode: any = document.querySelector('#file');
  //   console.log('Input node:', inputNode.files);

  //   if (inputNode.files && inputNode.files.length > 0) {
  //     console.log('Files selected:', inputNode.files);
  //     return Array.from(inputNode.files).map((file: File) => file.name);
  //   } else {
  //     return [];
  //   }
  // }

  // addAttachment(file: File): void {
  //   if (file && file.size > 0) {
  //     this.attachments.push(file);
  //     this.emailFormGroup.get('attachments')?.setValue(this.attachments);
  //     console.log('Attachment added:', file);
  //   } else {
  //     console.error('Invalid file selected:', file);
  //   }
  // }

}
